<script setup>
import { ref, computed } from 'vue';
import { useMainStore } from '@/stores/index';

const props = defineProps({
  title: {
    type: String,
    default: ''
  },
  items: {
    type: Array,
    default: () => []
  },
  groups: {
    type: Array,
    default: () => []
  },
  loading: {
    type: Boolean,
    default: false
  },
  searchPlaceholder: {
    type: String,
    default: 'Assunto, protocolo...'
  },
  searchFilterKeys: {
    type: Array,
    default: () => ['subject', 'protocol', 'typeTitle']
  },
  selectable: {
    type: Boolean,
    default: false
  }
});

const emit = defineEmits(['search', 'archived', 'unarchived']);

const Store = useMainStore();

const searchQuery = ref('');
const currentPage = ref(1);
const itemsPerPage = ref(10);
const collapsedGroups = ref({});
const selectedItemIds = ref([]);
const showArchiveBatchModal = ref(false);
const isArchiving = ref(false);

const toggleGroup = (groupId) => {
  collapsedGroups.value[groupId] = !collapsedGroups.value[groupId];
};

const isGroupCollapsed = (groupId) => {
  return !!collapsedGroups.value[groupId];
};

const handleSearchInput = () => {
  currentPage.value = 1;
  emit('search', searchQuery.value);
};

const hasGroups = computed(() => props.groups && props.groups.length > 0);

const filterItemList = (itemList) => {
  const query = searchQuery.value.trim().toLowerCase();
  if (!query) return itemList;

  return itemList.filter((item) => {
    return props.searchFilterKeys.some((key) => {
      const val = item[key];
      return val && String(val).toLowerCase().includes(query);
    });
  });
};

const filteredItems = computed(() => {
  if (hasGroups.value) return [];
  return filterItemList(props.items);
});

const filteredGroups = computed(() => {
  if (!hasGroups.value) return [];
  return props.groups
    .map((group) => ({
      ...group,
      filteredItems: filterItemList(group.items || [])
    }))
    .filter((group) => group.filteredItems.length > 0);
});

const totalItems = computed(() => {
  if (hasGroups.value) {
    return filteredGroups.value.reduce((acc, g) => acc + g.filteredItems.length, 0);
  }
  return filteredItems.value.length;
});

const totalPages = computed(() => Math.ceil(totalItems.value / itemsPerPage.value) || 1);

const paginatedItems = computed(() => {
  if (hasGroups.value) return [];
  const start = (currentPage.value - 1) * itemsPerPage.value;
  return filteredItems.value.slice(start, start + itemsPerPage.value);
});

const goToPage = (page) => {
  if (page >= 1 && page <= totalPages.value) {
    currentPage.value = page;
  }
};

const toggleSelectAll = (event, itemList) => {
  if (event.target.checked) {
    const ids = itemList.map((i) => i.id);
    selectedItemIds.value = Array.from(new Set([...selectedItemIds.value, ...ids]));
  } else {
    const idsToRemove = new Set(itemList.map((i) => i.id));
    selectedItemIds.value = selectedItemIds.value.filter((id) => !idsToRemove.has(id));
  }
};

const isAllSelected = (itemList) => {
  if (!itemList.length) return false;
  return itemList.every((i) => selectedItemIds.value.includes(i.id));
};

const toggleSelectItem = (id) => {
  const idx = selectedItemIds.value.indexOf(id);
  if (idx > -1) {
    selectedItemIds.value.splice(idx, 1);
  } else {
    selectedItemIds.value.push(id);
  }
};

const isUnarchiveAction = computed(() => {
  if (selectedItemIds.value.length === 0) return false;

  const allDocs = hasGroups.value
    ? props.groups.flatMap((g) => g.items || [])
    : props.items;

  const selectedDocs = allDocs.filter((doc) => selectedItemIds.value.includes(doc.id));

  return selectedDocs.length > 0 && selectedDocs.every((doc) => doc.status === 'filed' || doc.archived || doc.statusText === 'Arquivado');
});

const confirmArchiveBatch = async () => {
  if (selectedItemIds.value.length === 0) return;

  isArchiving.value = true;
  try {
    const idsToProcess = [...selectedItemIds.value];
    
    if (isUnarchiveAction.value) {
      if (typeof Store.UnarchiveBatch === 'function') {
        await Store.UnarchiveBatch(idsToProcess);
      } else {
        await Store.ArchiveBatch(idsToProcess);
      }
      showArchiveBatchModal.value = false;
      selectedItemIds.value = [];
      emit('unarchived', idsToProcess.length);
    } else {
      await Store.ArchiveBatch(idsToProcess);
      showArchiveBatchModal.value = false;
      selectedItemIds.value = [];
      emit('archived', idsToProcess.length);
    }
  } catch (error) {
    console.error('Erro ao processar documentos em lote:', error);
  } finally {
    isArchiving.value = false;
  }
};
</script>

<template>
  <div class="card-table-container p-3 p-md-4">
    <div class="row g-3 mb-3 mb-md-4 align-items-end">
      <div class="col-12 col-md-5 col-lg-4">
        <label class="filter-label mb-1">Buscar</label>
        <div class="search-input-wrapper">
          <input
            type="text"
            v-model="searchQuery"
            @input="handleSearchInput"
            class="form-control input-search"
            :placeholder="searchPlaceholder"
          />
          <font-awesome-icon :icon="['fas', 'magnifying-glass']" class="search-icon" />
        </div>
      </div>

      <slot name="extra-filters"></slot>
    </div>

    <div
      v-if="selectable && selectedItemIds.length > 0"
      class="batch-action-bar d-flex justify-content-between align-items-center p-2 px-3 mb-3"
    >
      <span class="font-docs fs-7 text-dark fw-500">
        <b>{{ selectedItemIds.length }}</b> item(ns) selecionado(s)
      </span>
      <button
        type="button"
        class="btn btn-orange-batch d-flex align-items-center gap-2 font-docs fs-7"
        @click="showArchiveBatchModal = true"
      >
        <font-awesome-icon :icon="['fas', isUnarchiveAction ? 'box-open' : 'box-archive']" />
        <span>{{ isUnarchiveAction ? 'Desarquivar selecionados' : 'Arquivar selecionados' }}</span>
      </button>
    </div>

    <div v-if="loading" class="text-center py-5">
      <ProgressBar mode="indeterminate" style="height: 6px;" class="w-50 mx-auto mb-3" />
      <span class="text-muted font-docs">Carregando documentos...</span>
    </div>

    <template v-else-if="totalItems > 0">
      <div v-if="hasGroups" class="groups-wrapper">
        <div
          v-for="group in filteredGroups"
          :key="group.id"
          class="group-section mb-3"
        >
          <div
            class="group-header d-flex justify-content-between align-items-center px-3 py-2"
            @click="toggleGroup(group.id)"
          >
            <span class="fw-bold text-dark font-docs font-size-14">{{ group.title }}</span>
            <font-awesome-icon
              :icon="['fas', isGroupCollapsed(group.id) ? 'chevron-down' : 'chevron-up']"
              class="text-secondary font-size-12"
            />
          </div>

          <div v-show="!isGroupCollapsed(group.id)" class="group-content">
            <div class="table-responsive d-none d-md-block">
              <table class="table table-custom align-middle">
                <thead>
                  <tr>
                    <th v-if="selectable" scope="col" class="cell-checkbox">
                      <input
                        type="checkbox"
                        class="check-custom"
                        :checked="isAllSelected(group.filteredItems)"
                        @change="toggleSelectAll($event, group.filteredItems)"
                      />
                    </th>
                    <slot name="header">
                      <th scope="col" style="width: 15%;">Protocolo</th>
                      <th scope="col" style="width: 40%;">Documento</th>
                      <th scope="col" style="width: 15%;">Data</th>
                      <th scope="col" style="width: 20%;">Situação</th>
                      <th scope="col" class="text-end" style="width: 10%;">Ações</th>
                    </slot>
                  </tr>
                </thead>
                <tbody>
                  <tr
                    v-for="(item, index) in group.filteredItems"
                    :key="item.id || index"
                    :class="['table-row-item', index % 2 === 0 ? 'row-even' : 'row-odd']"
                  >
                    <td v-if="selectable" class="cell-checkbox">
                      <input
                        type="checkbox"
                        class="check-custom"
                        :checked="selectedItemIds.includes(item.id)"
                        @change="toggleSelectItem(item.id)"
                      />
                    </td>
                    <slot name="row" :item="item">
                      <td class="cell-doc font-docs">
                        {{ item.protocol || '-' }}
                      </td>
                      <td class="cell-text font-docs text-truncate" style="max-width: 350px;">
                        {{ item.subject || 'Sem assunto' }}
                      </td>
                      <td class="cell-text font-docs">
                        {{ item.formattedDate || '-' }}
                      </td>
                      <td class="cell-text font-docs">
                        {{ item.statusText || '-' }}
                      </td>
                      <td class="text-end cell-actions">
                        <slot name="actions" :item="item"></slot>
                      </td>
                    </slot>
                  </tr>
                </tbody>
              </table>
            </div>

            <div class="mobile-cards-wrapper d-block d-md-none pt-2">
              <div
                v-for="(item, index) in group.filteredItems"
                :key="item.id || index"
                class="mobile-card mb-2 p-3"
                :class="index % 2 === 0 ? 'row-even' : 'row-odd'"
              >
                <div class="d-flex justify-content-between align-items-center mb-1">
                  <div class="d-flex align-items-center gap-2">
                    <input
                      v-if="selectable"
                      type="checkbox"
                      class="check-custom"
                      :checked="selectedItemIds.includes(item.id)"
                      @change="toggleSelectItem(item.id)"
                    />
                    <span class="fw-bold font-docs text-dark font-size-14">
                      {{ item.typeTitle || item.protocol || 'Documento' }}
                    </span>
                  </div>
                  <span class="text-muted font-docs font-size-12">
                    {{ item.formattedDate || '-' }}
                  </span>
                </div>

                <div class="text-secondary font-docs mb-2 text-truncate-2 font-size-13">
                  {{ item.subject || 'Sem assunto' }}
                </div>

                <div class="d-flex justify-content-between align-items-center border-top pt-2 mt-1">
                  <span class="badge bg-light text-dark font-docs font-size-12">
                    {{ item.statusText || '' }}
                  </span>
                  <div>
                    <slot name="actions" :item="item"></slot>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <template v-else>
        <div class="table-responsive d-none d-md-block">
          <table class="table table-custom align-middle">
            <thead>
              <tr>
                <th v-if="selectable" scope="col" class="cell-checkbox">
                  <input
                    type="checkbox"
                    class="check-custom"
                    :checked="isAllSelected(paginatedItems)"
                    @change="toggleSelectAll($event, paginatedItems)"
                  />
                </th>
                <slot name="header">
                  <th scope="col" style="width: 25%;">Documento</th>
                  <th scope="col" style="width: 50%;">Assunto</th>
                  <th scope="col" style="width: 15%;">Data</th>
                  <th scope="col" class="text-end" style="width: 10%;">Ações</th>
                </slot>
              </tr>
            </thead>
            <tbody>
              <tr
                v-for="(item, index) in paginatedItems"
                :key="item.id || index"
                :class="['table-row-item', index % 2 === 0 ? 'row-even' : 'row-odd']"
              >
                <td v-if="selectable" class="cell-checkbox">
                  <input
                    type="checkbox"
                    class="check-custom"
                    :checked="selectedItemIds.includes(item.id)"
                    @change="toggleSelectItem(item.id)"
                  />
                </td>
                <slot name="row" :item="item">
                  <td class="cell-doc font-docs">
                    {{ item.typeTitle || item.document_type || 'Documento' }}
                  </td>
                  <td class="cell-text font-docs text-truncate" style="max-width: 450px;">
                    {{ item.subject || 'Sem assunto' }}
                  </td>
                  <td class="cell-text font-docs">
                    {{ item.formattedDate || '-' }}
                  </td>
                  <td class="text-end cell-actions">
                    <slot name="actions" :item="item"></slot>
                  </td>
                </slot>
              </tr>
            </tbody>
          </table>
        </div>

        <div class="mobile-cards-wrapper d-block d-md-none">
          <div
            v-for="(item, index) in paginatedItems"
            :key="item.id || index"
            class="mobile-card mb-2 p-3"
            :class="index % 2 === 0 ? 'row-even' : 'row-odd'"
          >
            <div class="d-flex justify-content-between align-items-center mb-1">
              <div class="d-flex align-items-center gap-2">
                <input
                  v-if="selectable"
                  type="checkbox"
                  class="check-custom"
                  :checked="selectedItemIds.includes(item.id)"
                  @change="toggleSelectItem(item.id)"
                />
                <span class="fw-bold font-docs text-dark font-size-14">
                  {{ item.typeTitle || item.document_type || 'Documento' }}
                </span>
              </div>
              <span class="text-muted font-docs font-size-12">
                {{ item.formattedDate || '-' }}
              </span>
            </div>

            <div class="text-secondary font-docs mb-2 text-truncate-2 font-size-13">
              {{ item.subject || 'Sem assunto' }}
            </div>

            <div class="d-flex justify-content-end align-items-center border-top pt-2 mt-1">
              <slot name="actions" :item="item"></slot>
            </div>
          </div>
        </div>
      </template>
    </template>

    <div v-else class="text-center py-5">
      <font-awesome-icon :icon="['fas', 'folder-open']" class="text-muted fs-1 mb-3" />
      <p class="text-muted font-docs">Nenhum documento encontrado.</p>
    </div>

    <div v-if="!loading && totalItems > 0" class="d-flex flex-wrap justify-content-between align-items-center mt-3 mt-md-4 pt-3 border-top gap-2">
      <div class="text-muted font-docs fs-7">
        Total <b>{{ totalItems }}</b> items
      </div>

      <div class="d-flex align-items-center gap-2 ms-auto">
        <ul class="pagination pagination-custom mb-0">
          <li class="page-item" :class="{ disabled: currentPage === 1 }">
            <button class="page-link" @click="goToPage(currentPage - 1)">‹</button>
          </li>
          <li
            v-for="page in totalPages"
            :key="page"
            class="page-item"
            :class="{ active: currentPage === page }"
          >
            <button class="page-link" @click="goToPage(page)">{{ page }}</button>
          </li>
          <li class="page-item" :class="{ disabled: currentPage === totalPages }">
            <button class="page-link" @click="goToPage(currentPage + 1)">›</button>
          </li>
        </ul>

        <select v-model="itemsPerPage" class="form-select form-select-sm select-per-page">
          <option :value="10">10 / pág</option>
          <option :value="20">20 / pág</option>
          <option :value="50">50 / pág</option>
        </select>
      </div>
    </div>

    <div
      v-if="showArchiveBatchModal"
      class="modal-backdrop-custom d-flex align-items-center justify-content-center"
    >
      <div class="modal-card p-4">
        <div class="text-center mb-3">
          <div class="icon-warning-circle mx-auto mb-2 d-flex align-items-center justify-content-center">
            <font-awesome-icon :icon="['fas', isUnarchiveAction ? 'box-open' : 'box-archive']" />
          </div>
          <h5 class="fw-bold text-dark font-docs mb-1">
            {{ isUnarchiveAction ? 'Desarquivar documentos?' : 'Arquivar documentos?' }}
          </h5>
          <p class="text-muted font-docs fs-7 mb-0">
            Deseja realmente {{ isUnarchiveAction ? 'desarquivar' : 'arquivar' }} os <b>{{ selectedItemIds.length }}</b> documento(s) selecionado(s)?
          </p>
        </div>

        <div class="d-flex gap-2 justify-content-end mt-4">
          <button
            type="button"
            class="btn btn-light-custom flex-grow-1 font-docs"
            :disabled="isArchiving"
            @click="showArchiveBatchModal = false"
          >
            Cancelar
          </button>
          <button
            type="button"
            class="btn btn-orange-custom flex-grow-1 font-docs d-flex align-items-center justify-content-center gap-2"
            :disabled="isArchiving"
            @click="confirmArchiveBatch"
          >
            <font-awesome-icon v-if="isArchiving" :icon="['fas', 'spinner']" class="fa-spin" />
            <span>{{ isArchiving ? 'Processando...' : (isUnarchiveAction ? 'Confirmar Desarquivamento' : 'Confirmar Arquivamento') }}</span>
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped>
.card-table-container {
  background-color: #ffffff;
  border-radius: 12px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.04);
}

.filter-label {
  font-size: 13px;
  color: #6c757d;
  font-weight: 500;
}

.search-input-wrapper {
  position: relative;
  display: flex;
  align-items: center;
}

.input-search {
  border-color: #dee2e6;
  font-size: 14px;
  border-radius: 6px;
  padding-right: 36px;
}

.input-search:focus {
  border-color: #f1860b;
  box-shadow: 0 0 0 0.2rem rgba(241, 134, 11, 0.15);
}

.search-icon {
  position: absolute;
  right: 12px;
  color: #a0aec0;
  pointer-events: none;
  font-size: 14px;
}

.batch-action-bar {
  background-color: #fff8f0;
  border: 1px solid #ffe8cc;
  border-radius: 8px;
}

.btn-orange-batch {
  background-color: #f1860b;
  color: #ffffff;
  border: none;
  border-radius: 6px;
  padding: 6px 14px;
  font-weight: 500;
}

.btn-orange-batch:hover {
  background-color: #d97405;
}

.cell-checkbox {
  width: 32px !important;
  min-width: 32px !important;
  max-width: 32px !important;
  text-align: center !important;
  padding-left: 8px !important;
  padding-right: 8px !important;
}

.check-custom {
  appearance: auto !important;
  -webkit-appearance: checkbox !important;
  width: 16px !important;
  height: 16px !important;
  margin: 0 auto !important;
  padding: 0 !important;
  cursor: pointer;
  accent-color: #f1860b;
  display: block;
  border-radius: 3px;
}

.group-header {
  background-color: #e9ecef;
  border-radius: 6px;
  cursor: pointer;
  user-select: none;
  transition: background-color 0.15s ease;
}

.group-header:hover {
  background-color: #dee2e6;
}

.table-custom {
  margin-bottom: 0;
  border-collapse: separate;
  border-spacing: 0;
}

.table-custom th {
  color: #000000;
  font-weight: 700;
  font-size: 14px;
  border: none;
  padding: 10px 12px;
  background-color: transparent;
}

.table-custom td {
  padding: 10px 12px;
  border: none !important;
  font-size: 13.5px;
  vertical-align: middle;
}

.cell-doc {
  color: #1a1a1a;
  font-weight: 500;
}

.cell-text {
  color: #2d3748 !important;
  font-weight: 400;
}

.row-even {
  background-color: #f0f2f5;
}

.row-odd {
  background-color: #ffffff;
}

.table-row-item:hover {
  background-color: #e2e8f0 !important;
  transition: background-color 0.15s ease-in-out;
}

.mobile-card {
  border-radius: 8px;
  border: 1px solid #edf2f7;
}

.font-size-14 {
  font-size: 14px;
}

.font-size-13 {
  font-size: 13px;
}

.font-size-12 {
  font-size: 12px;
}

.text-truncate-2 {
  display: -webkit-box;
  -webkit-line-clamp: 2;
  line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
}

.pagination-custom .page-link {
  color: #f1860b;
  border-color: #dee2e6;
  font-size: 13px;
  padding: 4px 10px;
  cursor: pointer;
  background-color: #ffffff;
}

.pagination-custom .page-item.active .page-link {
  background-color: #f1860b;
  border-color: #f1860b;
  color: #ffffff;
  font-weight: 600;
}

.select-per-page {
  width: 95px;
  font-size: 12px;
  border-color: #dee2e6;
  padding: 4px 6px;
}

.modal-backdrop-custom {
  position: fixed;
  top: 0;
  left: 0;
  width: 100vw;
  height: 100vh;
  background-color: rgba(0, 0, 0, 0.45);
  z-index: 1050;
  padding: 16px;
}

.modal-card {
  background-color: #ffffff;
  border-radius: 12px;
  width: 100%;
  max-width: 400px;
  box-shadow: 0 10px 25px rgba(0, 0, 0, 0.15);
}

.icon-warning-circle {
  width: 48px;
  height: 48px;
  border-radius: 50%;
  background-color: #fff8f0;
  color: #f1860b;
  font-size: 20px;
}

.btn-light-custom {
  background-color: #f8f9fa;
  border: 1px solid #dee2e6;
  color: #495057;
  padding: 8px 16px;
  border-radius: 6px;
}

.btn-orange-custom {
  background-color: #f1860b;
  color: #ffffff;
  border: none;
  padding: 8px 16px;
  border-radius: 6px;
}

.btn-orange-custom:hover {
  background-color: #d97405;
}
</style>